import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../hooks/useAuth'
import { Users, Plus, Edit, Trash2, Eye, Calendar, Award, Mail, UserCheck, UserX, Upload, X, BookOpen, BarChart2, Palette, Download, CalendarRange, Search, ChevronDown, FileUp, RefreshCw, AlertTriangle } from 'lucide-react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import ReactDOM from 'react-dom';
import StatisticsModule from '../components/StatisticsModule';
import EventsAdminModule from '../components/EventsAdminModule';
import TechniquesAdminModule from '../components/TechniquesAdminModule';
import AppearanceAdminModule from '../components/AppearanceAdminModule';
import PageHeader from '../components/PageHeader';
import ConfirmDialog from './ConfirmDialog';
import Modal from './Modal';
import AdminDateInput from './AdminDateInput';

interface Member {
  id: number
  email: string | null
  firstName: string
  lastName: string
  dateOfBirth: string | null
  sex: 'FEMININ' | 'MASCULIN' | 'AUTRE' | null
  address: string | null
  postalCode: string | null
  city: string | null
  licenseNumber: string | null
  category: MemberCategory | null
  profileImage: string | null
  experienceYears: number
  isSeasonMember: boolean
  isAdmin: boolean
  role: string
  beltLevel: string
  age: number | null
  createdAt: string
  updatedAt: string
  dateArrivee: string | null
  accountStatus: 'MISSING_EMAIL' | 'PENDING_ACTIVATION' | 'ACTIVE'
  invitationSentAt: string | null
  seasonMemberships: Array<{
    id: number
    seasonId: number
    enrolledAt: string
  }>
}

interface Season {
  id: number
  label: string
  startDate: string
  endDate: string
  isCurrent: boolean
  memberCount: number
}

interface MemberFormData {
  email: string
  firstName: string
  lastName: string
  dateOfBirth: string
  sex: '' | 'FEMININ' | 'MASCULIN' | 'AUTRE'
  address: string
  postalCode: string
  city: string
  licenseNumber: string
  category: '' | MemberCategory
  profileImage: string
  seasonIds: number[]
  isAdmin: boolean
  role: string
  beltLevel: string
  dateArrivee: string
}

type MemberCategory = 'MINI_POUSSIN' | 'POUSSIN' | 'PUPILLE' | 'BENJAMIN' | 'MINIME' | 'CADET' | 'JUNIOR' | 'SENIOR'

type CsvImportAction = 'CREATE' | 'UPDATE' | 'ENROLL_ONLY' | 'UNCHANGED' | 'ERROR'

interface CsvImportChange {
  field: string
  from: string | number | null
  to: string | number
}

interface CsvImportRow {
  rowNumber: number
  action: CsvImportAction
  matchedUserId: number | null
  values: Record<string, string | number>
  changes: CsvImportChange[]
  errors: string[]
}

interface CsvImportPreview {
  fileHash: string
  previewHash: string
  warnings: string[]
  rows: CsvImportRow[]
  summary: Record<CsvImportAction, number> & { total: number }
}

interface CsvImportResult {
  summary: CsvImportPreview['summary'] & {
    invitationsSent: number
    invitationsFailed: number
    invitationsSkippedNoEmail: number
  }
  invitationFailures: Array<{ userId: number; rowNumber: number; message: string }>
}

const formatSeasonDate = (value: string) => new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: 'UTC'
}).format(new Date(value))

const csvActionLabels: Record<CsvImportAction, string> = {
  CREATE: 'Création',
  UPDATE: 'Mise à jour',
  ENROLL_ONLY: 'Inscription seule',
  UNCHANGED: 'Inchangé',
  ERROR: 'Erreur'
}

const csvActionClasses: Record<CsvImportAction, string> = {
  CREATE: 'bg-blue-100 text-blue-800',
  UPDATE: 'bg-amber-100 text-amber-800',
  ENROLL_ONLY: 'bg-violet-100 text-violet-800',
  UNCHANGED: 'bg-gray-100 text-gray-700',
  ERROR: 'bg-red-100 text-red-800'
}

const csvFieldLabels: Record<string, string> = {
  firstName: 'Prénom', lastName: 'Nom', dateOfBirth: 'Naissance', email: 'Email', sex: 'Sexe',
  address: 'Adresse', postalCode: 'Code postal', city: 'Ville', licenseNumber: 'Numéro de licence', category: 'Catégorie',
  dateArrivee: 'Arrivée au club', experienceYears: 'Expérience', role: 'Rôle', beltLevel: 'Ceinture'
}

const MembersAdminModule = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [members, setMembers] = useState<Member[]>([])
  const [seasons, setSeasons] = useState<Season[]>([])
  const [selectedSeasonId, setSelectedSeasonId] = useState<number | null>(null)
  const [seasonListSearch, setSeasonListSearch] = useState('')
  const [memberSeasonSearch, setMemberSeasonSearch] = useState('')
  const [showSeasonPicker, setShowSeasonPicker] = useState(false)
  const [showMemberSeasonPicker, setShowMemberSeasonPicker] = useState(false)
  const [memberFilter, setMemberFilter] = useState<'all' | 'registered' | 'unregistered'>('all')
  const [memberSearch, setMemberSearch] = useState('')
  const [membershipModal, setMembershipModal] = useState<{ mode: 'add' | 'remove'; season: Season } | null>(null)
  const [membershipModalMembers, setMembershipModalMembers] = useState<Member[]>([])
  const [membershipModalSearch, setMembershipModalSearch] = useState('')
  const [membershipModalSelection, setMembershipModalSelection] = useState<number[]>([])
  const [loadingMembershipModal, setLoadingMembershipModal] = useState(false)
  const [savingMemberships, setSavingMemberships] = useState(false)
  const [showSeasonForm, setShowSeasonForm] = useState(false)
  const [editingSeason, setEditingSeason] = useState<Season | null>(null)
  const [seasonFormData, setSeasonFormData] = useState({ label: '', startDate: '', endDate: '' })
  const [memberToDelete, setMemberToDelete] = useState<Member | null>(null)
  const [seasonToDelete, setSeasonToDelete] = useState<Season | null>(null)
  const [seasonToActivate, setSeasonToActivate] = useState<Season | null>(null)
  const [csvImportSeason, setCsvImportSeason] = useState<Season | null>(null)
  const [csvImportFile, setCsvImportFile] = useState<File | null>(null)
  const [csvImportPreview, setCsvImportPreview] = useState<CsvImportPreview | null>(null)
  const [csvImportResult, setCsvImportResult] = useState<CsvImportResult | null>(null)
  const [previewingCsv, setPreviewingCsv] = useState(false)
  const [committingCsv, setCommittingCsv] = useState(false)
  const [beltHistoryToDelete, setBeltHistoryToDelete] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingMember, setEditingMember] = useState<Member | null>(null)
  const [uploadingImage, setUploadingImage] = useState(false)
  const [showMembers, setShowMembers] = useState(true)
  const [showPosts, setShowPosts] = useState(false)
  const [showStats, setShowStats] = useState(false)
  const [showEvents, setShowEvents] = useState(false)
  const [showTechniques, setShowTechniques] = useState(false);
  const [showAppearance, setShowAppearance] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null)
  // Ajoute la déclaration manquante de formData pour les membres
  const [formData, setFormData] = useState<MemberFormData>({
    email: '',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    sex: '',
    address: '',
    postalCode: '',
    city: '',
    licenseNumber: '',
    category: '',
    profileImage: '',
    seasonIds: [],
    isAdmin: false,
    role: 'ADHERENT',
    beltLevel: 'BLANCHE',
    dateArrivee: ''
  })
  // Gestion des posts
  const [posts, setPosts] = useState<any[]>([])
  const [loadingPosts, setLoadingPosts] = useState(true)
  const [showPostForm, setShowPostForm] = useState(false)
  const [editingPost, setEditingPost] = useState<any | null>(null)
  const [postFormData, setPostFormData] = useState({ title: '', content: '' })
  // État pour le modal de montée de grade massive
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  // Pour chaque membre actif, steps = nombre de grades à monter (par défaut 1)
  const [upgradeSteps, setUpgradeSteps] = useState<{ [userId: number]: number }>({});
  const [upgradeDates, setUpgradeDates] = useState<{ [userId: number]: string }>({});
  const [beltHistory, setBeltHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showAddBeltModal, setShowAddBeltModal] = useState(false);
  const [newBelt, setNewBelt] = useState('');
  const [newBeltDate, setNewBeltDate] = useState('');
  const [newBeltComment, setNewBeltComment] = useState('');
  // Pour la montée de grade massive : date unique
  const [bulkUpgradeDate, setBulkUpgradeDate] = useState('');
  const [upgradeComments, setUpgradeComments] = useState<Record<number, string>>({});
  // 1. Ajoute un état pour les passages de ceinture à ajouter lors de la création
  const [pendingBeltHistory, setPendingBeltHistory] = useState<any[]>([]);

  // Remet BLANCHE dans la liste des niveaux de ceinture
  const beltLevels = [
    { value: 'BLANCHE', label: 'Blanche' },
    { value: 'JAUNE', label: 'Jaune' },
    { value: 'ORANGE', label: 'Orange' },
    { value: 'VERTE', label: 'Verte' },
    { value: 'BLEUE', label: 'Bleue' },
    { value: 'MARRON', label: 'Marron' },
    { value: 'NOIRE', label: 'Noire' }
  ]
  const roles = [
    { value: 'ADHERENT', label: 'Adhérent' },
    { value: 'SENSEI', label: 'Sensei' }
  ]
  const categories: Array<{ value: MemberCategory; label: string }> = [
    { value: 'MINI_POUSSIN', label: 'Mini-poussin' },
    { value: 'POUSSIN', label: 'Poussin' },
    { value: 'PUPILLE', label: 'Pupille' },
    { value: 'BENJAMIN', label: 'Benjamin' },
    { value: 'MINIME', label: 'Minime' },
    { value: 'CADET', label: 'Cadet' },
    { value: 'JUNIOR', label: 'Junior' },
    { value: 'SENIOR', label: 'Senior' }
  ]

  useEffect(() => {
    if (user) fetchSeasons()
  }, [user])

  // Gestion des posts
  useEffect(() => {
    if (showPosts) fetchPosts()
  }, [showPosts])

  // Gestion des membres
  // Le rechargement est piloté uniquement par l'ouverture du module et la saison sélectionnée.
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (showMembers && selectedSeasonId) fetchMembers()
  }, [showMembers, selectedSeasonId])
  /* eslint-enable react-hooks/exhaustive-deps */

  // Charger l'historique quand on édite un membre
  useEffect(() => {
    if (editingMember) {
      setLoadingHistory(true);
      axios.get(`/api/users/${editingMember.id}/belt-history`)
        .then(res => setBeltHistory(res.data))
        .catch(() => setBeltHistory([]))
        .finally(() => setLoadingHistory(false));
    } else {
      setBeltHistory([]);
    }
  }, [editingMember]);

  // Ajout manuel d'une entrée
  const handleAddBeltHistory = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!newBelt) return;
    if (!editingMember) {
      // Création : vérifie l'ordre logique à l'ajout
      if (pendingBeltHistory.some(h => h.beltLevel === newBelt)) {
        toast.error('Ce passage de ceinture a déjà été ajouté.');
        return;
      }
      const beltOrder = ['BLANCHE', 'JAUNE', 'ORANGE', 'VERTE', 'BLEUE', 'MARRON', 'NOIRE'];
      const newIdx = beltOrder.indexOf(newBelt);
      if (newIdx === -1) {
        toast.error('Ceinture inconnue.');
        return;
      }
      if (!newBeltDate) {
        toast.error('La date de passage doit être renseignée.');
        return;
      }
      // Ajout temporaire pour vérifier l'ordre logique
      const allBelts = [...pendingBeltHistory, { beltLevel: newBelt, date: newBeltDate, comment: newBeltComment.trim() }];
      // Trie par niveau de ceinture (pas par date)
      const sorted = [...allBelts].sort((a, b) => beltOrder.indexOf(a.beltLevel) - beltOrder.indexOf(b.beltLevel));
      for (let i = 1; i < sorted.length; i++) {
        const prev = sorted[i - 1];
        const curr = sorted[i];
        const prevIdx = beltOrder.indexOf(prev.beltLevel);
        const currIdx = beltOrder.indexOf(curr.beltLevel);
        if (currIdx <= prevIdx) {
          toast.error(`L'ordre des ceintures est incohérent (ex : ${curr.beltLevel.toLowerCase()} avant ${prev.beltLevel.toLowerCase()}).`);
          return;
        }
      }
      setPendingBeltHistory(prev => [...prev, { beltLevel: newBelt, date: newBeltDate, comment: newBeltComment.trim() }]);
      toast.success('Passage de ceinture ajouté (sera enregistré à la création) !');
      setShowAddBeltModal(false);
      setNewBelt('');
      setNewBeltDate('');
      setNewBeltComment('');
      return;
    }
    // Edition classique
    try {
      if (beltHistory.some(h => h.beltLevel === newBelt)) {
        toast.error('Ce passage de ceinture a déjà été ajouté.');
        return;
      }
      await axios.post(`/api/users/${editingMember.id}/belt-history`, {
        beltLevel: newBelt,
        date: newBeltDate || undefined,
        comment: newBeltComment.trim()
      });
      toast.success('Passage de ceinture ajouté !');
      setShowAddBeltModal(false);
      setNewBelt('');
      setNewBeltDate('');
      setNewBeltComment('');
      // Rafraîchir l'historique localement
      const res = await axios.get(`/api/users/${editingMember.id}/belt-history`);
      setBeltHistory(res.data);
    } catch (err: any) {
      // Correction : ne jamais afficher d'erreur de mot de passe ici
      const msg = err?.response?.data?.message || 'Erreur lors de l\'ajout';
      if (msg.toLowerCase().includes('mot de passe')) {
        toast.error('Erreur inattendue lors de l\'ajout du passage de ceinture.');
      } else {
        toast.error(msg);
      }
    }
  };

  // Ouvre le formulaire pour ajouter un membre
  const handleAddMember = () => {
    setEditingMember(null);
    setMemberSeasonSearch('');
    setShowMemberSeasonPicker(false);
    resetForm();
    setShowForm(true);
  };

  // Ouvre le formulaire pour modifier un membre
  const handleEditMember = (member: Member) => {
    setEditingMember(member);
    setMemberSeasonSearch('');
    setShowMemberSeasonPicker(false);
    setFormData({
      email: member.email ?? '',
      firstName: member.firstName,
      lastName: member.lastName,
      dateOfBirth: member.dateOfBirth ? member.dateOfBirth.split('T')[0] : '',
      sex: member.sex || '',
      address: member.address || '',
      postalCode: member.postalCode || '',
      city: member.city || '',
      licenseNumber: member.licenseNumber || '',
      category: member.category || '',
      profileImage: member.profileImage || '',
      seasonIds: member.seasonMemberships.map(membership => membership.seasonId),
      isAdmin: member.isAdmin,
      role: member.role,
      beltLevel: member.beltLevel,
      dateArrivee: member.dateArrivee ? member.dateArrivee.split('T')[0] : (member.createdAt ? member.createdAt.split('T')[0] : '')
    });
    setShowForm(true);
  };

  // Ferme le formulaire membre
  const cleanupPendingProfileImage = async (imagePath: string) => {
    const savedImage = editingMember?.profileImage || ''
    if (!imagePath || imagePath === savedImage) return
    try {
      await axios.delete('/api/upload', { data: { path: imagePath } })
    } catch (error) {
      if (!axios.isAxiosError(error) || ![404, 409].includes(error.response?.status ?? 0)) {
        console.error('Impossible de nettoyer la photo temporaire :', error)
      }
    }
  }

  const closeMemberForm = async () => {
    await cleanupPendingProfileImage(formData.profileImage)
    setShowForm(false);
    setEditingMember(null);
    resetForm();
    setPendingBeltHistory([]);
    setMemberSeasonSearch('');
    setShowMemberSeasonPicker(false);
  };

  // Ferme la section de gestion des membres
  const closeMembersSection = () => {
    setShowMembers(false);
    setShowForm(false);
    setEditingMember(null);
  };

  const fetchMembers = async () => {
    if (!selectedSeasonId) return
    try {
      setLoading(true)
      const response = await axios.get('/api/users/admin', { params: { seasonId: selectedSeasonId } })
      setMembers(response.data)
    } catch (error) {
      console.error('Erreur lors du chargement des membres:', error)
      toast.error('Erreur lors du chargement des membres')
    } finally {
      setLoading(false)
    }
  }

  const fetchSeasons = async () => {
    try {
      const response = await axios.get<Season[]>('/api/seasons')
      setSeasons(response.data)
      setSelectedSeasonId(current => {
        if (current && response.data.some(season => season.id === current)) return current
        return response.data.find(season => season.isCurrent)?.id || response.data[0]?.id || null
      })
    } catch (error) {
      console.error('Erreur lors du chargement des saisons:', error)
      toast.error('Erreur lors du chargement des saisons')
    }
  }

  const handleSeasonSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    try {
      if (editingSeason) {
        await axios.put(`/api/seasons/${editingSeason.id}`, seasonFormData)
        toast.success('Saison mise à jour')
      } else {
        const response = await axios.post<Season>('/api/seasons', seasonFormData)
        setSelectedSeasonId(response.data.id)
        toast.success('Saison créée')
      }
      setShowSeasonForm(false)
      setEditingSeason(null)
      setSeasonFormData({ label: '', startDate: '', endDate: '' })
      await fetchSeasons()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erreur lors de la sauvegarde de la saison')
    }
  }

  const handleSetCurrentSeason = async (season: Season) => {
    if (season.isCurrent) return
    try {
      await axios.patch(`/api/seasons/${season.id}/current`)
      toast.success(`${season.label} est maintenant la saison courante`)
      await fetchSeasons()
      await fetchMembers()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erreur lors du changement de saison')
    }
  }

  const handleDeleteSeason = async (season: Season) => {
    try {
      await axios.delete(`/api/seasons/${season.id}`)
      toast.success('Saison supprimée')
      await fetchSeasons()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Suppression impossible')
    }
  }

  const openMembershipModal = async (season: Season, mode: 'add' | 'remove') => {
    setSelectedSeasonId(season.id)
    setMembershipModal({ season, mode })
    setMembershipModalSearch('')
    setMembershipModalSelection([])
    setMembershipModalMembers([])
    setLoadingMembershipModal(true)
    try {
      const response = await axios.get<Member[]>('/api/users/admin', { params: { seasonId: season.id } })
      setMembershipModalMembers(response.data)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erreur lors du chargement des adhérents')
      setMembershipModal(null)
    } finally {
      setLoadingMembershipModal(false)
    }
  }

  const closeMembershipModal = () => {
    if (savingMemberships) return
    setMembershipModal(null)
    setMembershipModalMembers([])
    setMembershipModalSearch('')
    setMembershipModalSelection([])
  }

  const submitMembershipBatch = async () => {
    if (!membershipModal || membershipModalSelection.length === 0) return
    setSavingMemberships(true)
    try {
      if (membershipModal.mode === 'add') {
        await axios.post(`/api/seasons/${membershipModal.season.id}/memberships`, { userIds: membershipModalSelection })
      } else {
        await axios.delete(`/api/seasons/${membershipModal.season.id}/memberships`, { data: { userIds: membershipModalSelection } })
      }
      toast.success(membershipModal.mode === 'add' ? 'Adhésion(s) enregistrée(s)' : 'Adhésion(s) retirée(s)')
      setMembershipModal(null)
      setMembershipModalMembers([])
      setMembershipModalSearch('')
      setMembershipModalSelection([])
      await Promise.all([fetchMembers(), fetchSeasons()])
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erreur lors de la mise à jour des adhésions')
    } finally {
      setSavingMemberships(false)
    }
  }

  const handleExportSeason = async (season: Season) => {
    try {
      const response = await axios.get(`/api/seasons/${season.id}/export.csv`, { responseType: 'blob' })
      const url = URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = url
      link.download = `adherents-${season.label}.csv`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Erreur lors de l'export CSV")
    }
  }

  const openCsvImport = (season: Season) => {
    setCsvImportSeason(season)
    setCsvImportFile(null)
    setCsvImportPreview(null)
    setCsvImportResult(null)
  }

  const closeCsvImport = () => {
    if (previewingCsv || committingCsv) return
    setCsvImportSeason(null)
    setCsvImportFile(null)
    setCsvImportPreview(null)
    setCsvImportResult(null)
  }

  const handleDownloadImportTemplate = async () => {
    try {
      const response = await axios.get('/api/seasons/import-template.csv', { responseType: 'blob' })
      const url = URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = url
      link.download = 'modele-import-adherents.csv'
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Téléchargement du modèle impossible')
    }
  }

  const handlePreviewCsv = async () => {
    if (!csvImportSeason || !csvImportFile) return
    setPreviewingCsv(true)
    setCsvImportResult(null)
    try {
      const payload = new FormData()
      payload.append('file', csvImportFile)
      const response = await axios.post<CsvImportPreview>(`/api/seasons/${csvImportSeason.id}/import/preview`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      setCsvImportPreview(response.data)
    } catch (error: any) {
      setCsvImportPreview(null)
      const details = error.response?.data?.details as string[] | undefined
      toast.error(details?.length ? `${error.response.data.message} : ${details.join(' · ')}` : error.response?.data?.message || 'Analyse du CSV impossible')
    } finally {
      setPreviewingCsv(false)
    }
  }

  const handleCommitCsv = async () => {
    if (!csvImportSeason || !csvImportFile || !csvImportPreview || csvImportPreview.summary.ERROR > 0) return
    setCommittingCsv(true)
    try {
      const payload = new FormData()
      payload.append('file', csvImportFile)
      payload.append('previewHash', csvImportPreview.previewHash)
      const response = await axios.post<CsvImportResult>(`/api/seasons/${csvImportSeason.id}/import/commit`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      setCsvImportResult(response.data)
      toast.success('Import CSV terminé')
      await Promise.all([fetchMembers(), fetchSeasons()])
    } catch (error: any) {
      if (error.response?.status === 409 && error.response?.data?.preview) {
        setCsvImportPreview(error.response.data.preview)
      }
      toast.error(error.response?.data?.message || 'Import CSV impossible')
    } finally {
      setCommittingCsv(false)
    }
  }

  const handleResendInvitation = async (member: Member) => {
    if (!member.email) {
      toast.error('Ajoutez une adresse email avant d’envoyer une invitation')
      return
    }
    try {
      await axios.post(`/api/users/${member.id}/invitation`)
      toast.success(`Invitation renvoyée à ${member.email}`)
      setCsvImportResult(current => current ? {
        ...current,
        summary: {
          ...current.summary,
          invitationsSent: current.summary.invitationsSent + (current.invitationFailures.some(failure => failure.userId === member.id) ? 1 : 0),
          invitationsFailed: Math.max(0, current.summary.invitationsFailed - (current.invitationFailures.some(failure => failure.userId === member.id) ? 1 : 0))
        },
        invitationFailures: current.invitationFailures.filter(failure => failure.userId !== member.id)
      } : current)
      await fetchMembers()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Renvoi de l’invitation impossible')
    }
  }

  const fetchPosts = async () => {
    try {
      setLoadingPosts(true)
      const response = await axios.get('/api/posts/admin')
      setPosts(response.data)
    } catch (error: any) {
      console.error('=== ERREUR FETCH POSTS ===')
      console.error('Error:', error)
      console.error('Response:', error.response)
      toast.error(`Erreur lors du chargement des posts: ${error.response?.data?.message || error.message}`)
    } finally {
      setLoadingPosts(false)
    }
  }

  const handleImageUpload = async (file: File) => {
    try {
      setUploadingImage(true)
      const uploadPayload = new FormData()
      uploadPayload.append('profileImage', file)

      const response = await axios.post('/api/upload/profile', uploadPayload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      await cleanupPendingProfileImage(formData.profileImage)
      setFormData(prev => ({
        ...prev,
        profileImage: response.data.imageUrl
      }))
      toast.success('Photo uploadée avec succès')
    } catch (error: any) {
      console.error('Erreur lors de l\'upload:', error)
      toast.error(error.response?.data?.message || 'Erreur lors de l\'upload de l\'image')
    } finally {
      setUploadingImage(false)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleImageUpload(file)
    }
  }

  const removeProfileImage = async () => {
    await cleanupPendingProfileImage(formData.profileImage)
    setFormData(prev => ({
      ...prev,
      profileImage: ''
    }))
  }

  // 1. Suppression d'un passage temporaire
  const handleDeletePendingBelt = (idx: number) => {
    setPendingBeltHistory(prev => prev.filter((_, i) => i !== idx));
  };
  // 2. Vérification de l'ordre des ceintures et des dates dans pendingBeltHistory
  const checkBeltHistoryOrder = (history: any[]) => {
    const beltOrder = ['BLANCHE', 'JAUNE', 'ORANGE', 'VERTE', 'BLEUE', 'MARRON', 'NOIRE'];
    // Trie par date
    const sorted = [...history].sort((a, b) => new Date(a.date || '').getTime() - new Date(b.date || '').getTime());
    let lastIdx = -1;
    for (const h of sorted) {
      const idx = beltOrder.indexOf(h.beltLevel);
      if (idx === -1) return `Ceinture inconnue : ${h.beltLevel}`;
      if (idx <= lastIdx) return `L'ordre des ceintures est incohérent (ex : orange avant jaune).`;
      lastIdx = idx;
    }
    return null;
  };

  // 3. Ajoute la vérification avant la création dans handleSubmit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Vérification de l'ordre des ceintures et des dates
    if (!editingMember && pendingBeltHistory.length > 1) {
      const err = checkBeltHistoryOrder(pendingBeltHistory);
      if (err) {
        toast.error(err);
        return;
      }
    }
    try {
      const submitData = { ...formData };
      let newUser = null;
      if (editingMember) {
        // Mise à jour
        await axios.put(`/api/users/${editingMember.id}`, submitData);
        toast.success('Membre mis à jour avec succès');
      } else {
        // Création
        const res = await axios.post('/api/users', submitData);
        newUser = res.data;
        // Ajout des passages de ceinture en attente
        for (const h of pendingBeltHistory) {
          await axios.post(`/api/users/${newUser.id}/belt-history`, h);
        }
        if (res.data.invitation?.status === 'FAILED') {
          toast.error('Membre créé, mais l’invitation n’a pas pu être envoyée. Vous pourrez la renvoyer depuis la liste.')
        } else {
          toast.success('Membre créé et invitation envoyée')
        }
      }
      setShowForm(false);
      setEditingMember(null);
      resetForm();
      setPendingBeltHistory([]);
      await Promise.all([fetchMembers(), fetchSeasons()]);
    } catch (error: any) {
      let msg = 'Erreur lors de la sauvegarde';
      if (error && error.response && error.response.data && error.response.data.message) {
        msg = error.response.data.message;
      } else if (typeof error === 'string') {
        msg = error;
      }
      toast.error(msg);
    }
  };

  const handleDelete = async (id: number) => {
    
    try {
      await axios.delete(`/api/users/${id}`)
      toast.success('Membre supprimé avec succès')
      await Promise.all([fetchMembers(), fetchSeasons()])
    } catch (error: any) {
      console.error('Erreur lors de la suppression:', error)
      toast.error(error.response?.data?.message || 'Erreur lors de la suppression')
    }
  }

  // Lors de l'édition d'un post
  const handleEditPost = (post: any) => {
    setEditingPost(post)
    setPostFormData({
      title: post.title,
      content: post.content
    })
    setShowPostForm(true)
  }

  // Lors de la création d'un nouveau post
  const handleNewPost = () => {
    setShowPostForm(true)
    setEditingPost(null)
    setPostFormData({ title: '', content: '' })
  }

  const handleDeletePost = async (id: number) => {
    try {
      const response = await axios.delete(`/api/posts/${id}`)
      toast.success('Post supprimé')
      fetchPosts()
    } catch (error: any) {
      console.error('=== ERREUR DELETE POST ===')
      console.error('Error:', error)
      console.error('Response:', error.response)
      toast.error(`Erreur lors de la suppression: ${error.response?.data?.message || error.message}`)
    }
  }

  // Envoie les données du post
  const handleSubmitPost = async (e: React.FormEvent) => {
    e.preventDefault()
    
    try {
      if (editingPost) {
        const response = await axios.put(`/api/posts/${editingPost.id}`, postFormData)
        toast.success('Post modifié')
      } else {
        const response = await axios.post('/api/posts', { ...postFormData, published: true })
        toast.success('Post créé')
      }
      setShowPostForm(false)
      setEditingPost(null)
      setPostFormData({ title: '', content: '' })
      fetchPosts()
    } catch (error: any) {
      console.error('=== ERREUR POST SUBMIT ===')
      console.error('Error object:', error)
      console.error('Error response:', error.response)
      console.error('Error message:', error.message)
      if (error.response) {
        console.error('Status:', error.response.status)
        console.error('Data:', error.response.data)
      }
      toast.error(`Erreur lors de la sauvegarde: ${error.response?.data?.message || error.message}`)
    }
  }

  const resetForm = () => {
    const currentSeason = seasons.find(season => season.isCurrent)
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      sex: '',
      address: '',
      postalCode: '',
      city: '',
      licenseNumber: '',
      category: '',
      profileImage: '',
      seasonIds: currentSeason ? [currentSeason.id] : [],
      isAdmin: false,
      role: 'ADHERENT',
      beltLevel: 'BLANCHE',
      dateArrivee: ''
    })
  }

  const getBeltColor = (beltLevel: string) => {
    const colors: { [key: string]: string } = {
      'BLANCHE': 'bg-white text-gray-800 border-gray-300',
      'JAUNE': 'bg-yellow-400 text-yellow-900',
      'ORANGE': 'bg-orange-500 text-white',
      'VERTE': 'bg-green-500 text-white',
      'BLEUE': 'bg-blue-500 text-white',
      'MARRON': 'bg-amber-700 text-white',
      'NOIRE': 'bg-gray-900 text-white'
    }
    return colors[beltLevel] || 'bg-gray-200 text-gray-800'
  }

  const getBeltLabel = (beltLevel: string) => {
    const belt = beltLevels.find(b => b.value === beltLevel)
    return belt ? belt.label : beltLevel
  }

  const getRoleLabel = (role: string) => {
    const roleObj = roles.find(r => r.value === role)
    return roleObj ? roleObj.label : role
  }

  const getRoleBadge = (role: string, isAdmin: boolean) => {
    if (isAdmin) {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">Administrateur</span>
    }
    if (role === 'SENSEI') {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">Sensei</span>
    }
    return <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Adhérent</span>
  }

  // Fonction pour construire l'URL complète des images
  const getImageUrl = (imagePath: string | null) => {
    if (!imagePath) return undefined
    return imagePath
  }

  // Ouvre le modal de montée de grade
  const handleOpenUpgradeModal = () => {
    const selectedSeason = seasons.find(season => season.id === selectedSeasonId)
    if (!selectedSeason?.isCurrent) {
      toast.error('Les montées de grade sont réservées à la saison courante')
      return
    }
    // Initialise steps à 0 pour chaque adhérent actif
    const initial: { [userId: number]: number } = {};
    members.filter(m => m.isSeasonMember).forEach(m => {
      initial[m.id] = 0;
    });
    setUpgradeSteps(initial);
    setBulkUpgradeDate('');
    setUpgradeComments({});
    setShowUpgradeModal(true);
  };
  // Calcule le grade futur
  const getFutureBelt = (current: string, steps: number) => {
    const idx = beltLevels.findIndex(b => b.value === current);
    const nextIdx = Math.min(idx + steps, beltLevels.length - 1);
    if (idx === -1 || nextIdx < 0 || nextIdx >= beltLevels.length) return '';
    const belt = beltLevels[nextIdx];
    return belt ? belt.label : '';
  };
  // Applique la montée de grade
  const handleUpgradeBelts = async () => {
    try {
      const upgrades = Object.entries(upgradeSteps)
        .filter(([userId, steps]) => steps > 0)
        .map(([userId, steps]) => ({
          userId: Number(userId),
          steps,
          date: bulkUpgradeDate || undefined,
          comment: (upgradeComments[Number(userId)] || '').trim()
        }));
      if (upgrades.length === 0) {
        toast.error('Aucune montée de grade sélectionnée');
        return;
      }
      await axios.post('/api/users/bulk-upgrade-belt', upgrades);
      toast.success('Montée de grade effectuée !');
      setShowUpgradeModal(false);
      setUpgradeComments({});
      fetchMembers();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erreur lors de la montée de grade');
    }
  };

  const handleDeleteBeltHistory = async (historyId: number) => {
    if (!editingMember) return;
    try {
      await axios.delete(`/api/users/${editingMember.id}/belt-history/${historyId}`);
      toast.success('Entrée supprimée');
      // Refresh
      const res = await axios.get(`/api/users/${editingMember.id}/belt-history`);
      setBeltHistory(res.data);
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Pour l'ajout manuel, filtre les ceintures : pas BLANCHE, pas déjà dans l'historique
  const getAvailableBelts = () => {
    const usedBelts = new Set((editingMember ? beltHistory : pendingBeltHistory).map(h => h.beltLevel));
    return beltLevels.filter(b => b.value !== 'BLANCHE' && !usedBelts.has(b.value));
  };

  // Pour la montée de grade massive
  const upgradeModalRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!showUpgradeModal) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowUpgradeModal(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showUpgradeModal]);

  // Pour le modal des posts
  const postModalRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!showPostForm) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowPostForm(false);
        setEditingPost(null);
        setPostFormData({ title: '', content: '' });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showPostForm]);

  const currentBeltHistory = editingMember ? beltHistory : pendingBeltHistory;

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  const selectedSeason = seasons.find(season => season.id === selectedSeasonId) || null
  const normalizedSearch = memberSearch.trim().toLocaleLowerCase('fr-FR')
  const filteredMembers = members.filter(member => {
    const matchesFilter = memberFilter === 'all'
      || (memberFilter === 'registered' && member.isSeasonMember)
      || (memberFilter === 'unregistered' && !member.isSeasonMember)
    const matchesSearch = !normalizedSearch
      || `${member.firstName} ${member.lastName} ${member.email || ''} ${member.licenseNumber || ''}`.toLocaleLowerCase('fr-FR').includes(normalizedSearch)
    return matchesFilter && matchesSearch
  })

  const normalizedMembershipSearch = membershipModalSearch.trim().toLocaleLowerCase('fr-FR')
  const membershipModalCandidates = membershipModalMembers.filter(member => {
    const matchesMode = membershipModal?.mode === 'add' ? !member.isSeasonMember : member.isSeasonMember
    const matchesSearch = !normalizedMembershipSearch
      || `${member.firstName} ${member.lastName} ${member.email || ''}`.toLocaleLowerCase('fr-FR').includes(normalizedMembershipSearch)
    return matchesMode && matchesSearch
  })
  const allMembershipCandidatesSelected = membershipModalCandidates.length > 0
    && membershipModalCandidates.every(member => membershipModalSelection.includes(member.id))
  const normalizedSeasonListSearch = seasonListSearch.trim().toLocaleLowerCase('fr-FR')
  const filteredSeasons = seasons.filter(season => !normalizedSeasonListSearch
    || season.label.toLocaleLowerCase('fr-FR').includes(normalizedSeasonListSearch))
  const normalizedMemberSeasonSearch = memberSeasonSearch.trim().toLocaleLowerCase('fr-FR')
  const filteredMemberSeasons = seasons.filter(season => !normalizedMemberSeasonSearch
    || season.label.toLocaleLowerCase('fr-FR').includes(normalizedMemberSeasonSearch))

  return (
    <div className="space-y-8">
      {/* Vignettes admin */}
      <div className="hidden">
        <div
          className="card cursor-pointer hover:shadow-xl transition-shadow flex flex-col items-center justify-center text-center py-10"
          onClick={() => {
            setShowMembers(!showMembers);
            if (!showMembers) {
              setShowPosts(false);
              setShowStats(false);
              setShowEvents(false);
              setShowTechniques(false);
              setShowAppearance(false);
              setShowForm(false);
              setShowPostForm(false);
            }
          }}
        >
          <Users className="w-12 h-12 text-primary-600 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Gestion des membres</h2>
          <p className="text-gray-600">Ajouter, modifier ou supprimer les membres du club</p>
        </div>
        <div
          className="card cursor-pointer hover:shadow-xl transition-shadow flex flex-col items-center justify-center text-center py-10"
          onClick={() => {
            setShowPosts(!showPosts);
            if (!showPosts) {
              setShowMembers(false);
              setShowStats(false);
              setShowEvents(false);
              setShowTechniques(false);
              setShowAppearance(false);
              setShowForm(false);
              setShowPostForm(false);
            }
          }}
        >
          <BookOpen className="w-12 h-12 text-primary-600 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Gestion des posts</h2>
          <p className="text-gray-600">Créer, éditer et supprimer les actualités du club</p>
        </div>
        <div
          className="card cursor-pointer hover:shadow-xl transition-shadow flex flex-col items-center justify-center text-center py-10"
          onClick={() => {
            setShowStats(!showStats);
            if (!showStats) {
              setShowMembers(false);
              setShowPosts(false);
              setShowEvents(false);
              setShowTechniques(false);
              setShowAppearance(false);
              setShowForm(false);
              setShowPostForm(false);
            }
          }}
        >
          <BarChart2 className="w-12 h-12 text-primary-600 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Statistiques</h2>
          <p className="text-gray-600">Voir les statistiques du club et de l'activité</p>
        </div>

        <div
          className="card cursor-pointer hover:shadow-xl transition-shadow flex flex-col items-center justify-center text-center py-10"
          onClick={() => {
            setShowEvents(!showEvents);
            if (!showEvents) {
              setShowMembers(false);
              setShowPosts(false);
              setShowStats(false);
              setShowTechniques(false);
              setShowAppearance(false);
              setShowForm(false);
              setShowPostForm(false);
            }
          }}
        >
          <Calendar className="w-12 h-12 text-primary-600 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Événements</h2>
          <p className="text-gray-600">Gérer les événements et les inscriptions</p>
        </div>
        <div
          className="card cursor-pointer hover:shadow-xl transition-shadow flex flex-col items-center justify-center text-center py-10"
          onClick={() => {
            setShowTechniques(!showTechniques);
            if (!showTechniques) {
              setShowMembers(false);
              setShowPosts(false);
              setShowStats(false);
              setShowEvents(false);
              setShowAppearance(false);
              setShowForm(false);
              setShowPostForm(false);
            }
          }}
        >
          <BookOpen className="w-12 h-12 text-primary-600 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Gestion des techniques</h2>
          <p className="text-gray-600">Ajouter, modifier ou supprimer les techniques du club</p>
        </div>
        <div
          className="card cursor-pointer hover:shadow-xl transition-shadow flex flex-col items-center justify-center text-center py-10"
          onClick={() => {
            setShowAppearance(!showAppearance);
            if (!showAppearance) {
              setShowMembers(false);
              setShowPosts(false);
              setShowStats(false);
              setShowEvents(false);
              setShowTechniques(false);
              setShowForm(false);
              setShowPostForm(false);
            }
          }}
        >
          <Palette className="w-12 h-12 text-primary-600 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Apparence du site</h2>
          <p className="text-gray-600">Personnaliser les images de fond de l'accueil et des pages</p>
        </div>
      </div>

      {/* Gestion des membres (affichée seulement si showMembers) */}
      {showMembers && (
        <div className="mt-8">
          <div className="card p-6">
            <div className="mb-6 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
              <div>
                <p className="eyebrow !mb-1">Vie associative</p>
                <h2 className="flex items-center gap-2 text-2xl font-bold text-gray-900"><Users className="w-6 h-6 text-primary-600" /> Gestion des adhérents</h2>
                <p className="mt-1 text-sm text-gray-600">Gérez les inscriptions, les coordonnées administratives et les exports saison par saison.</p>
              </div>
              <button
                type="button"
                className="btn-secondary flex items-center gap-2"
                onClick={() => {
                  setEditingSeason(null)
                  setSeasonFormData({ label: '', startDate: '', endDate: '' })
                  setShowSeasonForm(true)
                }}
              >
                <CalendarRange className="h-4 w-4" /> Nouvelle saison
              </button>
            </div>
            <div className="space-y-6">
              <section className="modal-section p-4" aria-labelledby="season-list-title">
                <div className="mb-3">
                  <h3 id="season-list-title" className="text-lg font-bold text-gray-900">Saison consultée</h3>
                  <p className="text-sm text-gray-600">Une seule saison pilote la liste des membres et les actions ci-dessous.</p>
                </div>

                {selectedSeason ? (
                  <>
                    <button
                      type="button"
                      className="flex min-h-16 w-full items-center justify-between gap-4 rounded-2xl border border-primary-900/10 bg-white p-4 text-left transition hover:border-primary-500/35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                      onClick={() => {
                        if (!showSeasonPicker) setSeasonListSearch('')
                        setShowSeasonPicker(current => !current)
                      }}
                      aria-expanded={showSeasonPicker}
                      aria-controls="season-picker-list"
                    >
                      <span className="min-w-0">
                        <span className="flex flex-wrap items-center gap-2">
                          <strong className="text-lg text-gray-900">{selectedSeason.label}</strong>
                          {selectedSeason.isCurrent && <span className="role-chip role-member"><UserCheck className="h-3.5 w-3.5" /> Saison courante</span>}
                        </span>
                        <span className="mt-1 block text-sm text-gray-600">
                          Du {formatSeasonDate(selectedSeason.startDate)} au {formatSeasonDate(selectedSeason.endDate)} · {selectedSeason.memberCount} adhérent{selectedSeason.memberCount !== 1 ? 's' : ''}
                        </span>
                      </span>
                      <span className="flex shrink-0 items-center gap-2 text-sm font-bold text-primary-700">
                        <span className="hidden sm:inline">Changer de saison</span>
                        <ChevronDown className={`h-5 w-5 transition-transform ${showSeasonPicker ? 'rotate-180' : ''}`} />
                      </span>
                    </button>

                    {showSeasonPicker && (
                      <div id="season-picker-list" className="mt-3 overflow-hidden rounded-2xl border border-primary-900/10 bg-white">
                        <div className="border-b border-primary-900/10 p-3">
                          <label className="relative block">
                            <span className="sr-only">Rechercher une saison</span>
                            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            <input
                              className="input-field !pl-10"
                              value={seasonListSearch}
                              onChange={event => setSeasonListSearch(event.target.value)}
                              placeholder="Rechercher une saison…"
                              autoFocus
                            />
                          </label>
                        </div>
                        <div className="max-h-64 overflow-y-auto overscroll-contain" role="listbox" aria-label="Choisir la saison consultée">
                          {filteredSeasons.map(season => {
                            const isSelected = season.id === selectedSeasonId
                            return (
                              <button
                                key={season.id}
                                type="button"
                                role="option"
                                aria-selected={isSelected}
                                className={`flex min-h-14 w-full items-center justify-between gap-3 border-b border-primary-900/10 px-4 py-3 text-left last:border-b-0 hover:ring-1 hover:ring-inset hover:ring-primary-500/25 ${isSelected ? 'ring-2 ring-inset ring-primary-500/25' : ''}`}
                                onClick={() => {
                                  setSelectedSeasonId(season.id)
                                  setShowSeasonPicker(false)
                                }}
                              >
                                <span>
                                  <span className="flex flex-wrap items-center gap-2 text-sm font-bold text-gray-900">
                                    {season.label}
                                    {season.isCurrent && <span className="role-chip role-member">Courante</span>}
                                  </span>
                                  <span className="mt-1 block text-xs text-gray-500">{season.memberCount} adhérent{season.memberCount !== 1 ? 's' : ''}</span>
                                </span>
                                {isSelected && <span className="text-xs font-bold text-primary-700">Sélectionnée</span>}
                              </button>
                            )
                          })}
                          {filteredSeasons.length === 0 && (
                            <p className="px-4 py-8 text-center text-sm font-semibold text-gray-500">Aucune saison ne correspond à cette recherche.</p>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="mt-4 flex flex-wrap gap-2 border-t border-primary-900/10 pt-4">
                      <button type="button" className="btn-primary" onClick={() => openMembershipModal(selectedSeason, 'add')}>
                        <Plus className="h-4 w-4" /> Ajouter des adhérents
                      </button>
                      <button type="button" className="btn-secondary" onClick={() => openMembershipModal(selectedSeason, 'remove')} disabled={selectedSeason.memberCount === 0}>
                        <UserX className="h-4 w-4" /> Retirer des adhérents
                      </button>
                      <button type="button" className="btn-secondary" onClick={() => handleExportSeason(selectedSeason)}>
                        <Download className="h-4 w-4" /> Exporter CSV
                      </button>
                      <button type="button" className="btn-secondary" onClick={() => openCsvImport(selectedSeason)}>
                        <FileUp className="h-4 w-4" /> Importer un CSV
                      </button>
                      {!selectedSeason.isCurrent && (
                        <button type="button" className="btn-secondary" onClick={() => setSeasonToActivate(selectedSeason)}>Définir comme courante</button>
                      )}
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => {
                          setEditingSeason(selectedSeason)
                          setSeasonFormData({
                            label: selectedSeason.label,
                            startDate: selectedSeason.startDate.split('T')[0],
                            endDate: selectedSeason.endDate.split('T')[0]
                          })
                          setShowSeasonForm(true)
                        }}
                        aria-label={`Modifier la saison ${selectedSeason.label}`}
                      ><Edit className="h-4 w-4" /></button>
                      <button
                        type="button"
                        className="btn-danger"
                        onClick={() => setSeasonToDelete(selectedSeason)}
                        disabled={selectedSeason.isCurrent || selectedSeason.memberCount > 0}
                        aria-label={`Supprimer la saison ${selectedSeason.label}`}
                      ><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </>
                ) : (
                  <p className="rounded-2xl border border-dashed border-primary-900/15 p-6 text-center text-sm font-semibold text-gray-600">Aucune saison n’a encore été créée.</p>
                )}
              </section>

              {showSeasonForm && (
                <form className="modal-section grid gap-4 p-4 md:grid-cols-4 md:items-end" onSubmit={handleSeasonSubmit}>
                  <div>
                    <label className="mb-1 block text-sm font-bold text-gray-700" htmlFor="season-label">Libellé</label>
                    <input id="season-label" className="input-field" value={seasonFormData.label} onChange={event => setSeasonFormData(data => ({ ...data, label: event.target.value }))} placeholder="2026–2027" required />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-bold text-gray-700" htmlFor="season-start">Début</label>
                    <AdminDateInput id="season-start" value={seasonFormData.startDate} onChange={startDate => setSeasonFormData(data => ({ ...data, startDate }))} required />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-bold text-gray-700" htmlFor="season-end">Fin</label>
                    <AdminDateInput id="season-end" value={seasonFormData.endDate} onChange={endDate => setSeasonFormData(data => ({ ...data, endDate }))} required />
                  </div>
                  <div className="flex gap-2">
                    <button type="submit" className="btn-primary flex-1">{editingSeason ? 'Enregistrer' : 'Créer'}</button>
                    <button type="button" className="btn-secondary" onClick={() => { setShowSeasonForm(false); setEditingSeason(null) }}>Annuler</button>
                  </div>
                </form>
              )}

              <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                <div className="grid min-w-0 flex-1 grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_13rem]">
                  <label className="relative min-w-0">
                    <span className="sr-only">Rechercher un adhérent</span>
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input className="input-field !pl-10" value={memberSearch} onChange={event => setMemberSearch(event.target.value)} placeholder="Nom, prénom, email ou licence…" />
                  </label>
                  <select className="input-field min-w-0" value={memberFilter} onChange={event => setMemberFilter(event.target.value as typeof memberFilter)} aria-label="Filtrer les adhérents">
                    <option value="all">Tous les comptes</option>
                    <option value="registered">Inscrits à la saison</option>
                    <option value="unregistered">Non inscrits</option>
                  </select>
                </div>
                <div className="flex flex-wrap gap-2">
                <button
                  onClick={handleOpenUpgradeModal}
                  className="btn-secondary flex items-center gap-2"
                  disabled={!selectedSeason?.isCurrent}
                >
                  <Award className="w-4 h-4" /> Monter les grades
                </button>
                <button
                  onClick={handleAddMember}
                  className="btn-primary flex items-center gap-2"
                >
                  <Plus size={20} />
                  Nouveau membre
                </button>
                </div>
              </div>

              <Modal
                isOpen={Boolean(csvImportSeason)}
                onClose={closeCsvImport}
                title={csvImportSeason ? `Importer des adhérents — ${csvImportSeason.label}` : 'Importer des adhérents'}
                eyebrow="Import CSV sécurisé"
                size="lg"
                footer={csvImportSeason ? (
                  csvImportResult ? (
                    <button type="button" className="btn-primary" onClick={closeCsvImport}>Terminer</button>
                  ) : (
                    <>
                      <button type="button" className="btn-secondary" onClick={closeCsvImport} disabled={previewingCsv || committingCsv}>Annuler</button>
                      {csvImportPreview ? (
                        <button
                          type="button"
                          className="btn-primary"
                          onClick={handleCommitCsv}
                          disabled={csvImportPreview.summary.ERROR > 0 || committingCsv}
                        >
                          {committingCsv ? 'Import en cours…' : `Confirmer l’import (${csvImportPreview.summary.total})`}
                        </button>
                      ) : (
                        <button type="button" className="btn-primary" onClick={handlePreviewCsv} disabled={!csvImportFile || previewingCsv}>
                          {previewingCsv ? 'Analyse en cours…' : 'Analyser le fichier'}
                        </button>
                      )}
                    </>
                  )
                ) : undefined}
              >
                {csvImportSeason && (
                  <div className="space-y-5">
                    {!csvImportResult && (
                      <section className="modal-section space-y-3 p-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <h3 className="font-bold text-gray-900">1. Sélectionner le fichier</h3>
                            <p className="text-sm text-gray-600">CSV UTF-8, UTF-16 ou Windows-1252/ANSI, 5 Mo et 5 000 lignes maximum. Seuls le nom et le prénom sont obligatoires et servent à retrouver l’adhérent. L’email et la naissance sont facultatifs.</p>
                          </div>
                          <button type="button" className="btn-secondary shrink-0" onClick={handleDownloadImportTemplate}>
                            <Download className="h-4 w-4" /> Télécharger le modèle
                          </button>
                        </div>
                        <input
                          type="file"
                          accept=".csv,text/csv"
                          className="input-field"
                          onChange={event => {
                            setCsvImportFile(event.target.files?.[0] ?? null)
                            setCsvImportPreview(null)
                            setCsvImportResult(null)
                          }}
                        />
                        {csvImportFile && <p className="text-xs font-semibold text-gray-500">{csvImportFile.name} · {(csvImportFile.size / 1024).toFixed(1)} Ko</p>}
                      </section>
                    )}

                    {csvImportPreview && !csvImportResult && (
                      <>
                        <section className="modal-section p-4">
                          <h3 className="mb-3 font-bold text-gray-900">2. Vérifier l’aperçu</h3>
                          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                            {(['CREATE', 'UPDATE', 'ENROLL_ONLY', 'UNCHANGED', 'ERROR'] as CsvImportAction[]).map(action => (
                              <div key={action} className="rounded-xl border border-primary-900/10 bg-white p-3 text-center">
                                <div className="text-2xl font-bold text-gray-900">{csvImportPreview.summary[action]}</div>
                                <div className="text-xs font-semibold text-gray-600">{csvActionLabels[action]}</div>
                              </div>
                            ))}
                          </div>
                          {csvImportPreview.warnings.map(warning => (
                            <div key={warning} className="mt-3 flex gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {warning}
                            </div>
                          ))}
                          {csvImportPreview.summary.ERROR > 0 && (
                            <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-800">
                              Corrigez toutes les lignes en erreur puis analysez à nouveau le fichier.
                            </div>
                          )}
                        </section>

                        <div className="max-h-96 overflow-auto rounded-xl border border-primary-900/10">
                          <table className="admin-table min-w-[760px]">
                            <thead>
                              <tr><th>Ligne</th><th>Adhérent</th><th>Action</th><th>Détails</th></tr>
                            </thead>
                            <tbody>
                              {csvImportPreview.rows.map(row => (
                                <tr key={row.rowNumber}>
                                  <td className="font-bold">{row.rowNumber}</td>
                                  <td>
                                    <div className="font-semibold text-gray-900">{row.values.firstName} {row.values.lastName}</div>
                                    <div className="text-xs text-gray-500">{row.values.email || row.values.licenseNumber || 'Sans email'}</div>
                                  </td>
                                  <td><span className={`rounded-full px-2 py-1 text-xs font-bold ${csvActionClasses[row.action]}`}>{csvActionLabels[row.action]}</span></td>
                                  <td className="max-w-md text-xs">
                                    {row.errors.length > 0 ? (
                                      <ul className="list-disc space-y-1 pl-4 text-red-700">{row.errors.map(error => <li key={error}>{error}</li>)}</ul>
                                    ) : row.changes.length > 0 ? (
                                      <ul className="space-y-1 text-gray-600">
                                        {row.changes.map(change => (
                                          <li key={change.field}><strong>{csvFieldLabels[change.field] || change.field} :</strong> {String(change.from ?? '—')} → {String(change.to)}</li>
                                        ))}
                                      </ul>
                                    ) : <span className="text-gray-500">Aucune donnée modifiée</span>}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}

                    {csvImportResult && (
                      <section className="modal-section space-y-4 p-5">
                        <div className="flex items-start gap-3">
                          <UserCheck className="mt-0.5 h-6 w-6 text-green-600" />
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">Import terminé</h3>
                            <p className="text-sm text-gray-600">
                              {csvImportResult.summary.CREATE} création(s), {csvImportResult.summary.UPDATE} mise(s) à jour et {csvImportResult.summary.ENROLL_ONLY} inscription(s) seule(s).
                            </p>
                            <p className="mt-1 text-sm text-gray-600">
                              {csvImportResult.summary.invitationsSent} invitation(s) envoyée(s), {csvImportResult.summary.invitationsFailed} en échec.
                            </p>
                            {csvImportResult.summary.invitationsSkippedNoEmail > 0 && (
                              <p className="mt-1 text-sm text-gray-600">
                                {csvImportResult.summary.invitationsSkippedNoEmail} invitation(s) non envoyée(s), faute d’adresse email.
                              </p>
                            )}
                          </div>
                        </div>
                        {csvImportResult.invitationFailures.length > 0 && (
                          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                            <h4 className="mb-2 font-bold text-amber-900">Invitations à renvoyer</h4>
                            <div className="space-y-2">
                              {csvImportResult.invitationFailures.map(failure => {
                                const member = members.find(item => item.id === failure.userId)
                                return (
                                  <div key={failure.userId} className="flex items-center justify-between gap-3 rounded-lg bg-white p-3 text-sm">
                                    <span>Ligne {failure.rowNumber}{member ? ` · ${member.firstName} ${member.lastName}` : ''}</span>
                                    {member && (
                                      <button type="button" className="btn-secondary" onClick={() => handleResendInvitation(member)}>
                                        <RefreshCw className="h-4 w-4" /> Renvoyer
                                      </button>
                                    )}
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        )}
                      </section>
                    )}
                  </div>
                )}
              </Modal>

              <Modal
                isOpen={Boolean(membershipModal)}
                onClose={closeMembershipModal}
                title={membershipModal
                  ? `${membershipModal.mode === 'add' ? 'Ajouter des adhérents' : 'Retirer des adhérents'} — ${membershipModal.season.label}`
                  : 'Gérer les inscriptions'}
                eyebrow={membershipModal?.mode === 'remove' ? 'Retrait de la saison' : 'Inscriptions saisonnières'}
                size="md"
                footer={membershipModal ? (
                  <>
                    <button type="button" className="btn-secondary" onClick={closeMembershipModal} disabled={savingMemberships}>Annuler</button>
                    <button
                      type="button"
                      className={membershipModal.mode === 'add' ? 'btn-primary' : 'btn-danger'}
                      onClick={submitMembershipBatch}
                      disabled={membershipModalSelection.length === 0 || savingMemberships}
                    >
                      {savingMemberships
                        ? 'Enregistrement…'
                        : membershipModal.mode === 'add'
                          ? `Ajouter (${membershipModalSelection.length})`
                          : `Retirer (${membershipModalSelection.length})`}
                    </button>
                  </>
                ) : undefined}
              >
                {membershipModal && (
                  <div className="space-y-4">
                    {membershipModal.mode === 'remove' && (
                      <div className="membership-removal-summary">
                        <span aria-hidden="true"><UserX /></span>
                        <div>
                          <strong>Sélectionnez les adhérents à retirer</strong>
                          <p>Les comptes restent dans l’annuaire du club ; seule leur inscription à la saison {membershipModal.season.label} sera supprimée.</p>
                        </div>
                      </div>
                    )}
                    <div>
                      <label className="relative block">
                        <span className="sr-only">Rechercher un adhérent</span>
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                        <input
                          className="input-field !pl-10"
                          value={membershipModalSearch}
                          onChange={event => setMembershipModalSearch(event.target.value)}
                          placeholder={membershipModal.mode === 'remove' ? 'Rechercher parmi les inscrits…' : 'Nom, prénom ou email…'}
                        />
                      </label>
                    </div>

                    {membershipModal.mode === 'remove' && membershipModal.season.isCurrent && (
                      <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-800" role="alert">
                        Retirer un adhérent de la saison courante bloque son accès dès son prochain appel privé. Les administrateurs restent exemptés.
                      </div>
                    )}

                    {loadingMembershipModal ? (
                      <div className="flex min-h-40 items-center justify-center" aria-live="polite">
                        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary-200 border-b-primary-600" />
                        <span className="sr-only">Chargement des adhérents</span>
                      </div>
                    ) : (
                      <div className="modal-section overflow-hidden">
                        <label className="flex min-h-12 cursor-pointer items-center gap-3 border-b border-primary-900/10 px-4 py-3 font-bold text-gray-700">
                          <input
                            type="checkbox"
                            className="h-5 w-5 accent-primary-600"
                            checked={allMembershipCandidatesSelected}
                            disabled={membershipModalCandidates.length === 0}
                            onChange={event => {
                              const visibleIds = membershipModalCandidates.map(member => member.id)
                              setMembershipModalSelection(current => event.target.checked
                                ? Array.from(new Set([...current, ...visibleIds]))
                                : current.filter(id => !visibleIds.includes(id)))
                            }}
                          />
                          <span>Sélectionner les résultats affichés</span>
                          <span className="ml-auto text-xs text-gray-500">{membershipModalSelection.length} sélectionné{membershipModalSelection.length !== 1 ? 's' : ''}</span>
                        </label>
                        <div className="max-h-80 overflow-y-auto">
                          {membershipModalCandidates.map(member => (
                            <label key={member.id} className="flex min-h-14 cursor-pointer items-center gap-3 border-b border-primary-900/10 px-4 py-3 last:border-b-0 hover:bg-primary-50/70">
                              <input
                                type="checkbox"
                                className="h-5 w-5 shrink-0 accent-primary-600"
                                checked={membershipModalSelection.includes(member.id)}
                                onChange={event => setMembershipModalSelection(current => event.target.checked
                                  ? [...current, member.id]
                                  : current.filter(id => id !== member.id))}
                              />
                              <span className="min-w-0 flex-1">
                                <span className="block truncate text-sm font-bold text-gray-900">{member.firstName} {member.lastName}</span>
                                <span className="block truncate text-xs text-gray-500">{member.email || 'Sans email'}</span>
                              </span>
                              {getRoleBadge(member.role, member.isAdmin)}
                            </label>
                          ))}
                          {membershipModalCandidates.length === 0 && (
                            <p className="px-4 py-10 text-center text-sm font-semibold text-gray-500">
                              {membershipModal.mode === 'add'
                                ? 'Aucun compte non inscrit ne correspond à cette recherche.'
                                : 'Aucun inscrit ne correspond à cette recherche.'}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Modal>

              {/* Formulaire d'ajout/modification membre */}
              <Modal
                isOpen={showForm}
                onClose={closeMemberForm}
                title={editingMember ? 'Modifier l’adhérent' : 'Nouvel adhérent'}
                eyebrow="Gestion des adhérents"
                size="lg"
                footer={(
                  <>
                    <button type="button" onClick={closeMemberForm} className="btn-secondary">Annuler</button>
                    <button type="submit" form="member-admin-form" className="btn-primary">{editingMember ? 'Mettre à jour' : 'Créer'}</button>
                  </>
                )}
              >
                    <form id="member-admin-form" onSubmit={handleSubmit} className="space-y-5">
                      <section className="form-section">
                        <div className="form-section-heading">
                          <div><h3>Identité et pratique</h3><p>Renseignez les informations utiles au suivi de l’adhérent.</p></div>
                        </div>
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="form-field">
                          <label className="form-label" htmlFor="member-email">
                            Email {!editingMember || editingMember.email ? '*' : <span className="form-label-optional">(optionnel)</span>}
                          </label>
                          <input
                            id="member-email"
                            type="email"
                            autoComplete="email"
                            required={!editingMember || Boolean(editingMember.email)}
                            placeholder="exemple@email.com"
                            value={formData.email}
                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                            className="input-field"
                          />
                        </div>
                        
                        {!editingMember && (
                          <div className="rounded-xl border border-primary-200 bg-primary-50 p-3 text-sm text-primary-900">
                            Aucun mot de passe provisoire ne sera créé. L’adhérent recevra un lien personnel valable 7 jours pour définir son mot de passe.
                          </div>
                        )}
                        
                        <div className="form-field">
                          <label className="form-label" htmlFor="member-first-name">
                            Prénom *
                          </label>
                          <input
                            id="member-first-name"
                            type="text"
                            autoComplete="given-name"
                            required
                            placeholder="Prénom du membre"
                            value={formData.firstName}
                            onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                            className="input-field"
                          />
                        </div>
                        
                        <div className="form-field">
                          <label className="form-label" htmlFor="member-last-name">
                            Nom *
                          </label>
                          <input
                            id="member-last-name"
                            type="text"
                            autoComplete="family-name"
                            required
                            placeholder="Nom de famille"
                            value={formData.lastName}
                            onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                            className="input-field"
                          />
                        </div>
                        
                        <div className="form-field">
                          <label className="form-label" htmlFor="member-birth-date">
                            Date de naissance
                          </label>
                          <AdminDateInput
                            id="member-birth-date"
                            max={new Date().toISOString().split('T')[0]}
                            value={formData.dateOfBirth}
                            onChange={dateOfBirth => setFormData({...formData, dateOfBirth})}
                          />
                        </div>

                        <div className="form-field">
                          <label className="form-label" htmlFor="member-sex">Sexe</label>
                          <select id="member-sex" value={formData.sex} onChange={event => setFormData({ ...formData, sex: event.target.value as MemberFormData['sex'] })} className="input-field">
                            <option value="">Non renseigné</option>
                            <option value="FEMININ">Féminin</option>
                            <option value="MASCULIN">Masculin</option>
                            <option value="AUTRE">Autre</option>
                          </select>
                        </div>

                        <div className="form-field md:col-span-2">
                          <label className="form-label" htmlFor="member-address">Adresse</label>
                          <input id="member-address" type="text" autoComplete="street-address" maxLength={255} value={formData.address} onChange={event => setFormData({ ...formData, address: event.target.value })} className="input-field" placeholder="Numéro et voie" />
                        </div>

                        <div className="form-field">
                          <label className="form-label" htmlFor="member-postal-code">Code postal</label>
                          <input id="member-postal-code" type="text" autoComplete="postal-code" inputMode="numeric" maxLength={5} pattern="[0-9]{5}" value={formData.postalCode} onChange={event => setFormData({ ...formData, postalCode: event.target.value.replace(/\D/g, '').slice(0, 5) })} className="input-field" placeholder="26000" />
                        </div>

                        <div className="form-field">
                          <label className="form-label" htmlFor="member-city">Ville</label>
                          <input id="member-city" type="text" autoComplete="address-level2" maxLength={100} value={formData.city} onChange={event => setFormData({ ...formData, city: event.target.value })} className="input-field" placeholder="Valence" />
                        </div>

                        <div className="form-field">
                          <label className="form-label" htmlFor="member-license-number">Numéro de licence</label>
                          <input id="member-license-number" type="text" maxLength={50} value={formData.licenseNumber} onChange={event => setFormData({ ...formData, licenseNumber: event.target.value })} className="input-field" placeholder="Numéro de licence FFKDA" />
                        </div>

                        <div className="form-field">
                          <label className="form-label" htmlFor="member-category">Catégorie</label>
                          <select id="member-category" value={formData.category} onChange={event => setFormData({ ...formData, category: event.target.value as MemberFormData['category'] })} className="input-field">
                            <option value="">Non renseignée</option>
                            {categories.map(category => <option key={category.value} value={category.value}>{category.label}</option>)}
                          </select>
                        </div>
                        
                        <div className="form-field">
                          <label className="form-label" htmlFor="member-experience">
                            Années d'expérience
                          </label>
                          <output id="member-experience" className="input-field" aria-live="polite">
                            {formData.seasonIds.length} an{formData.seasonIds.length !== 1 ? 's' : ''}
                          </output>
                          <p className="form-help">Calcul automatique selon le nombre de saisons auxquelles l’adhérent est inscrit.</p>
                        </div>
                        
                        <div className="form-field">
                          <label className="form-label" htmlFor="member-belt">
                            Ceinture
                          </label>
                          <select
                            id="member-belt"
                            value={formData.beltLevel}
                            onChange={(e) => setFormData({...formData, beltLevel: e.target.value})}
                            className="input-field"
                          >
                            {beltLevels.map(level => (
                              <option key={level.value} value={level.value}>{level.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        <fieldset className="modal-section p-4 md:col-span-2">
                          <legend className="px-1 text-sm font-bold text-gray-700">Rôle et permissions</legend>
                          <div className="grid gap-2 sm:grid-cols-2">
                            <label className="form-check-card">
                              <input
                                type="checkbox"
                                className="h-5 w-5 accent-primary-600"
                                checked={formData.isAdmin}
                                onChange={e => setFormData({ ...formData, isAdmin: e.target.checked })}
                              />
                              <span className="text-sm text-gray-700">Administrateur</span>
                            </label>
                            <label className="form-check-card">
                              <input
                                type="checkbox"
                                className="h-5 w-5 accent-primary-600"
                                checked={formData.role === 'SENSEI'}
                                onChange={e => setFormData({ ...formData, role: e.target.checked ? 'SENSEI' : 'ADHERENT' })}
                              />
                              <span className="text-sm text-gray-700">Sensei</span>
                            </label>
                          </div>
                        </fieldset>
                        <div className="form-field">
                          <label className="form-label" htmlFor="member-arrival-date">Date d'arrivée au club</label>
                          <AdminDateInput
                            id="member-arrival-date"
                            max={new Date().toISOString().split('T')[0]}
                            value={formData.dateArrivee}
                            onChange={dateArrivee => setFormData({ ...formData, dateArrivee })}
                          />
                        </div>
                      </div>
                      </section>

                      <fieldset className="modal-section p-4">
                        <legend className="px-1 text-sm font-bold text-gray-700">Inscriptions aux saisons</legend>
                        <button
                          type="button"
                          className="flex min-h-14 w-full items-center justify-between gap-3 rounded-xl border border-primary-900/10 bg-white p-3 text-left hover:border-primary-500/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                          onClick={() => {
                            if (!showMemberSeasonPicker) setMemberSeasonSearch('')
                            setShowMemberSeasonPicker(current => !current)
                          }}
                          aria-expanded={showMemberSeasonPicker}
                          aria-controls="member-season-picker"
                        >
                          <span className="min-w-0">
                            <span className="block text-sm font-bold text-gray-900">
                              {formData.seasonIds.length} saison{formData.seasonIds.length !== 1 ? 's' : ''} sélectionnée{formData.seasonIds.length !== 1 ? 's' : ''}
                            </span>
                            <span className="mt-1 block truncate text-xs text-gray-500">
                              {seasons
                                .filter(season => formData.seasonIds.includes(season.id))
                                .map(season => season.label)
                                .join(', ') || 'Aucune inscription saisonnière'}
                            </span>
                          </span>
                          <span className="flex shrink-0 items-center gap-2 text-xs font-bold text-primary-700">
                            <span className="hidden sm:inline">Modifier</span>
                            <ChevronDown className={`h-5 w-5 transition-transform ${showMemberSeasonPicker ? 'rotate-180' : ''}`} />
                          </span>
                        </button>

                        {showMemberSeasonPicker && (
                          <div id="member-season-picker" className="mt-3 overflow-hidden rounded-xl border border-primary-900/10 bg-white">
                            <div className="border-b border-primary-900/10 p-3">
                              <label className="relative block">
                                <span className="sr-only">Rechercher dans les saisons du membre</span>
                                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                <input
                                  className="input-field !pl-10"
                                  value={memberSeasonSearch}
                                  onChange={event => setMemberSeasonSearch(event.target.value)}
                                  placeholder="Rechercher une saison…"
                                  autoFocus
                                />
                              </label>
                            </div>
                            <div className="max-h-64 overflow-y-auto overscroll-contain" role="list" aria-label="Saisons disponibles pour ce membre">
                              {filteredMemberSeasons.map(season => (
                                <label key={season.id} role="listitem" className="flex min-h-14 cursor-pointer items-center gap-3 border-b border-primary-900/10 px-4 py-3 last:border-b-0 hover:ring-1 hover:ring-inset hover:ring-primary-500/25">
                                  <input
                                    type="checkbox"
                                    className="h-5 w-5 shrink-0 accent-primary-600"
                                    checked={formData.seasonIds.includes(season.id)}
                                    onChange={event => setFormData(current => ({
                                      ...current,
                                      seasonIds: event.target.checked
                                        ? [...current.seasonIds, season.id]
                                        : current.seasonIds.filter(id => id !== season.id)
                                    }))}
                                  />
                                  <span className="min-w-0 flex-1">
                                    <span className="flex flex-wrap items-center gap-2 text-sm font-bold text-gray-900">
                                      {season.label}
                                      {season.isCurrent && <span className="role-chip role-member">Courante</span>}
                                    </span>
                                    <span className="mt-1 block text-xs text-gray-500">
                                      {formatSeasonDate(season.startDate)} – {formatSeasonDate(season.endDate)}
                                    </span>
                                  </span>
                                </label>
                              ))}
                              {seasons.length > 0 && filteredMemberSeasons.length === 0 && (
                                <p className="py-6 text-center text-sm font-semibold text-gray-500">Aucune saison ne correspond à cette recherche.</p>
                              )}
                              {seasons.length === 0 && <p className="py-6 text-center text-sm font-semibold text-gray-500">Aucune saison disponible.</p>}
                            </div>
                          </div>
                        )}
                      </fieldset>
                      
                      {/* Upload de photo de profil */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Photo de profil
                        </label>
                        <div className="flex items-center gap-4">
                          {formData.profileImage ? (
                            <div className="relative">
                              <img
                                src={getImageUrl(formData.profileImage)}
                                alt="Photo de profil"
                                className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
                              />
                              <button
                                type="button"
                                onClick={() => void removeProfileImage()}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ) : (
                            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center border-2 border-dashed border-gray-300">
                              <Upload className="w-8 h-8 text-gray-400" />
                            </div>
                          )}
                          
                          <div className="flex-1">
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept="image/jpeg,image/png,image/gif,image/webp"
                              onChange={handleFileChange}
                              className="hidden"
                            />
                            <button
                              type="button"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={uploadingImage}
                              className="btn-secondary flex items-center gap-2"
                            >
                              <Upload className="w-4 h-4" />
                              {uploadingImage ? 'Upload en cours...' : 'Choisir une photo'}
                            </button>
                            <p className="text-xs text-gray-500 mt-1">
                              Formats acceptés : JPG, PNG, GIF, WebP (max 5MB)
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Historique des ceintures */}
                      <h3 className="text-lg font-semibold mt-6 mb-2 flex items-center gap-2"><Award className="w-5 h-5"/> Historique des ceintures</h3>
                      {loadingHistory ? (
                        <div className="text-gray-500">Chargement...</div>
                      ) : currentBeltHistory.length === 0 ? (
                        <div className="text-gray-400 italic">Aucun historique</div>
                      ) : (
                        <ul className="space-y-1 mb-2">
                          {currentBeltHistory.map((h, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm">
                              <span className={`px-2 py-1 rounded border ${getBeltColor(h.beltLevel)}`}>{getBeltLabel(h.beltLevel)}</span>
                              <span className="min-w-0 flex-1">
                                <span className="flex items-center gap-1 text-gray-500"><Calendar className="w-4 h-4"/>{h.date ? new Date(h.date).toLocaleDateString('fr-FR') : ''}</span>
                                {h.comment && <span className="mt-1 block whitespace-pre-wrap text-gray-600">{h.comment}</span>}
                              </span>
                              {!editingMember && (
                                <button type="button" className="ml-2 text-red-500 hover:text-red-700" title="Supprimer" onClick={() => handleDeletePendingBelt(i)}><Trash2 className="w-4 h-4"/></button>
                              )}
                              {editingMember && (
                                <button type="button" className="icon-button icon-button-danger ml-2" title="Supprimer" aria-label="Supprimer ce passage de ceinture" onClick={() => setBeltHistoryToDelete(h.id)}><Trash2 className="w-4 h-4"/></button>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                      <button type="button" className="btn-secondary mb-4" onClick={() => setShowAddBeltModal(value => !value)} aria-expanded={showAddBeltModal}>Ajouter un passage de ceinture</button>
                      {showAddBeltModal && (
                        <div className="rounded-2xl border border-primary-900/10 bg-primary-50/70 p-4 dark:border-white/10 dark:bg-white/5">
                          <h4 className="font-semibold mb-3">Nouveau passage de ceinture</h4>
                          <div className="grid gap-3 sm:grid-cols-2">
                            <select value={newBelt} onChange={e => { setNewBelt(e.target.value); console.log('select newBelt', e.target.value); }} className="input-field" required>
                              <option value="">Sélectionner une ceinture</option>
                              {getAvailableBelts().map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                            </select>
                            <AdminDateInput value={newBeltDate} onChange={setNewBeltDate} />
                          </div>
                          <label className="mt-3 block">
                            <span className="mb-1 block text-sm font-medium text-gray-700">Commentaire <span className="font-normal text-gray-500">(facultatif)</span></span>
                            <textarea value={newBeltComment} onChange={e => setNewBeltComment(e.target.value)} maxLength={1000} rows={3} className="input-field w-full" placeholder="Ex. passage validé lors du stage régional…" />
                          </label>
                            <div className="mt-3 flex gap-2 justify-end">
                              <button type="button" className="btn-secondary" onClick={() => setShowAddBeltModal(false)}>Annuler</button>
                              <button type="button" className="btn-primary" onClick={() => void handleAddBeltHistory()}>Ajouter</button>
                            </div>
                        </div>
                      )}
                      
                    </form>
              </Modal>
              {/* Modal montée de grade massive */}
              <Modal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} title="Montée de grade massive" eyebrow="Progression" size="lg">
                    <div ref={upgradeModalRef}>
                      <div className="mb-4 flex flex-wrap items-center gap-2">
                        <label className="font-medium">Date du passage :</label>
                        <AdminDateInput value={bulkUpgradeDate} onChange={setBulkUpgradeDate} className="w-56" />
                      </div>
                      <div className="overflow-x-auto">
                        <table className="admin-table">
                          <thead>
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Membre</th>
                              <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Grade actuel</th>
                              <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Monter de</th>
                              <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Grade futur</th>
                              <th>Commentaire individuel (facultatif)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {members.filter(m => m.isSeasonMember).map(m => {
                              const idx = beltLevels.findIndex(b => b.value === m.beltLevel);
                              const maxSteps = beltLevels.length - 1 - idx;
                              return (
                                <tr key={m.id}>
                                  <td className="px-4 py-2 whitespace-nowrap">{m.firstName} {m.lastName}</td>
                                  <td className="px-4 py-2 text-center">{getBeltLabel(m.beltLevel)}</td>
                                  <td className="px-4 py-2 text-center">
                                    <div className="flex items-center justify-center gap-2">
                                      <button
                                        type="button"
                                        className="btn-secondary px-2 py-1"
                                        disabled={upgradeSteps[m.id] <= 0}
                                        onClick={() => setUpgradeSteps(s => ({ ...s, [m.id]: Math.max(0, s[m.id] - 1) }))}
                                      >-</button>
                                      <input
                                        type="number"
                                        max={maxSteps}
                                        value={upgradeSteps[m.id]}
                                        onChange={e => {
                                          let val = parseInt(e.target.value);
                                          if (isNaN(val)) val = 0;
                                          val = Math.max(0, Math.min(val, maxSteps));
                                          setUpgradeSteps(s => ({ ...s, [m.id]: val }));
                                        }}
                                        className="w-12 text-center border rounded"
                                      />
                                      <button
                                        type="button"
                                        className="btn-secondary px-2 py-1"
                                        disabled={upgradeSteps[m.id] >= maxSteps}
                                        onClick={() => setUpgradeSteps(s => ({ ...s, [m.id]: Math.min(maxSteps, s[m.id] + 1) }))}
                                      >+</button>
                                    </div>
                                  </td>
                                  <td className="px-4 py-2 text-center font-semibold">{getFutureBelt(m.beltLevel, upgradeSteps[m.id])}</td>
                                  <td className="min-w-56 px-4 py-2">
                                    <textarea
                                      aria-label={`Commentaire pour ${m.firstName} ${m.lastName}`}
                                      value={upgradeComments[m.id] || ''}
                                      onChange={event => setUpgradeComments(comments => ({ ...comments, [m.id]: event.target.value }))}
                                      disabled={!upgradeSteps[m.id]}
                                      maxLength={1000}
                                      rows={2}
                                      className="input-field !min-h-0 w-full disabled:opacity-50"
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div className="flex justify-end gap-2 mt-6">
                        <button
                          className="btn-secondary"
                          onClick={() => setShowUpgradeModal(false)}
                        >Annuler</button>
                        <button
                          className="btn-primary"
                          onClick={handleUpgradeBelts}
                        >Valider</button>
                      </div>
                    </div>
              </Modal>
              {/* Tableau des membres */}
              <div className="card !p-0 overflow-hidden">
                <div className="data-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Membre
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Âge
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ceinture
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Licence / catégorie
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Expérience
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Statut
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMembers.map((member) => (
                        <tr key={member.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                {member.profileImage ? (
                                  <img
                                    className="h-10 w-10 rounded-full object-cover"
                                    src={getImageUrl(member.profileImage)}
                                    alt={`${member.firstName} ${member.lastName}`}
                                  />
                                ) : (
                                  <div className="h-10 w-10 rounded-full bg-primary-600 flex items-center justify-center">
                                    <span className="text-white font-bold text-sm">
                                      {member.firstName[0]}{member.lastName[0]}
                                    </span>
                                  </div>
                                )}
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {member.firstName} {member.lastName}
                                </div>
                                <div className="text-sm text-gray-500">{member.email || 'Sans email'}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {member.age ? `${member.age} ans` : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getBeltColor(member.beltLevel)}`}>
                              {getBeltLabel(member.beltLevel)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div>{member.licenseNumber || '—'}</div>
                            <div className="text-xs text-gray-500">{member.category ? categories.find(category => category.value === member.category)?.label : 'Non renseignée'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {member.experienceYears} an{member.experienceYears > 1 ? 's' : ''}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col gap-1">
                              {getRoleBadge(member.role, member.isAdmin)}
                              {member.isSeasonMember ? (
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 flex items-center gap-1">
                                  <UserCheck className="w-3 h-3" />
                                  Inscrit
                                </span>
                              ) : (
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 flex items-center gap-1">
                                  <UserX className="w-3 h-3" />
                                  Non inscrit
                                </span>
                              )}
                              {member.accountStatus === 'PENDING_ACTIVATION' && (
                                <span className="flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800">
                                  <Mail className="h-3 w-3" /> En attente d’activation
                                </span>
                              )}
                              {member.accountStatus === 'MISSING_EMAIL' && (
                                <span className="flex items-center gap-1 rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-800">
                                  <Mail className="h-3 w-3" /> Email manquant
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex gap-2">
                              {member.accountStatus === 'PENDING_ACTIVATION' && member.email && (
                                <button
                                  onClick={() => handleResendInvitation(member)}
                                  className="icon-button"
                                  title="Renvoyer l’invitation"
                                  aria-label={`Renvoyer l’invitation à ${member.firstName} ${member.lastName}`}
                                >
                                  <RefreshCw className="h-4 w-4" />
                                </button>
                              )}
                              <button
                                onClick={() => handleEditMember(member)}
                                className="icon-button"
                                title="Modifier"
                                aria-label={`Modifier ${member.firstName} ${member.lastName}`}
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setMemberToDelete(member)}
                                className="icon-button icon-button-danger"
                                title="Supprimer"
                                aria-label={`Supprimer ${member.firstName} ${member.lastName}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {filteredMembers.length === 0 && (
                        <tr>
                          <td colSpan={7} className="px-6 py-10 text-center text-sm font-semibold text-gray-500">Aucun adhérent ne correspond à ces critères.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Gestion des posts (affichée seulement si showPosts) */}
      {showPosts && (
        <>
          {/* Formulaire de création/édition en MODAL */}
          {showPostForm && ReactDOM.createPortal(
            <div className="modal-backdrop" onMouseDown={e => {
              if (e.target === e.currentTarget) {
                setShowPostForm(false);
                setEditingPost(null);
                setPostFormData({ title: '', content: '' });
              }
            }}>
              <div ref={postModalRef} className="modal-panel modal-panel-md !block !overflow-y-auto p-6 relative" role="dialog" aria-modal="true" aria-label="Publication">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">
                    {editingPost ? 'Modifier le post' : 'Nouveau post'}
                  </h2>
                  <button onClick={() => setShowPostForm(false)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <form onSubmit={handleSubmitPost} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Titre</label>
                    <input
                      type="text"
                      className="w-full border rounded-lg px-3 py-2"
                      placeholder="Titre de l'actualité"
                      value={postFormData.title}
                      onChange={e => setPostFormData({ ...postFormData, title: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Contenu</label>
                    <textarea
                      className="w-full border rounded-lg px-3 py-2 min-h-[100px]"
                      placeholder="Contenu de l'actualité..."
                      value={postFormData.content}
                      onChange={e => setPostFormData({ ...postFormData, content: e.target.value })}
                      required
                    />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button type="submit" className="btn-primary">
                      {editingPost ? 'Mettre à jour' : 'Créer'}
                    </button>
                    <button type="button" className="btn-secondary" onClick={() => setShowPostForm(false)}>
                      Annuler
                    </button>
                  </div>
                </form>
              </div>
            </div>,
            document.body
          )}
          {/* Liste des posts */}
          <div className="card mt-6">
            <h2 className="text-xl font-semibold mb-4">Posts existants</h2>
            {loadingPosts ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center text-gray-500">Aucun post pour le moment.</div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Titre</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Auteur</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3"></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {posts.map(post => (
                    <tr key={post.id}>
                      <td className="px-6 py-4 whitespace-nowrap font-medium">{post.title}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{post.author.firstName} {post.author.lastName}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{new Date(post.createdAt).toLocaleDateString('fr-FR')}</td>
                      <td className="px-6 py-4 whitespace-nowrap flex gap-2">
                        <button onClick={() => handleEditPost(post)} className="text-primary-600 hover:text-primary-900" title="Modifier">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDeletePost(post.id)} className="text-red-600 hover:text-red-900" title="Supprimer">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="flex justify-end mt-4">
              <button
                className="btn-primary flex items-center gap-2"
                onClick={() => {
                  setShowPostForm(true)
                  setEditingPost(null)
                  setPostFormData({ title: '', content: '' })
                }}
              >
                <Plus className="w-4 h-4" /> Nouveau post
              </button>
            </div>
          </div>
        </>
      )}

      {/* Statistiques (affichée seulement si showStats) */}
      {showStats && (
        <div className="mt-8">
          <div className="card p-6">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2"><BarChart2 className="w-6 h-6" /> Statistiques</h2>
            <StatisticsModule />
          </div>
        </div>
      )}

      {/* Gestion des événements (affichée seulement si showEvents) */}
      {showEvents && (
        <div className="mt-8">
          <div className="card p-6">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2"><Calendar className="w-6 h-6" /> Gestion des événements</h2>
            <EventsAdminModule />
          </div>
        </div>
      )}

      {/* Gestion des techniques (affichée seulement si showTechniques) */}
      {showTechniques && (
        <div className="mt-8">
          <div className="card p-6">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2"><BookOpen className="w-6 h-6" /> Gestion des techniques</h2>
            <TechniquesAdminModule />
          </div>
        </div>
      )}

      {showAppearance && (
        <div className="mt-8">
          <div className="card p-6">
            <h2 className="mb-6 flex items-center gap-2 text-2xl font-bold"><Palette className="h-6 w-6" /> Apparence du site</h2>
            <AppearanceAdminModule />
          </div>
        </div>
      )}
      <ConfirmDialog isOpen={memberToDelete !== null} title="Supprimer le membre" message={`Le compte de ${memberToDelete?.firstName ?? ''} ${memberToDelete?.lastName ?? ''} sera définitivement supprimé.`} confirmLabel="Supprimer" onConfirm={async () => { if (memberToDelete) await handleDelete(memberToDelete.id); setMemberToDelete(null) }} onCancel={() => setMemberToDelete(null)} />
      <ConfirmDialog isOpen={seasonToDelete !== null} title="Supprimer la saison" message={`La saison ${seasonToDelete?.label ?? ''} sera supprimée.`} confirmLabel="Supprimer" onConfirm={async () => { if (seasonToDelete) await handleDeleteSeason(seasonToDelete); setSeasonToDelete(null) }} onCancel={() => setSeasonToDelete(null)} />
      <ConfirmDialog isOpen={seasonToActivate !== null} title="Changer de saison courante" message={`Définir ${seasonToActivate?.label ?? ''} comme saison courante ? Les accès adhérents basculeront immédiatement.`} confirmLabel="Définir comme courante" onConfirm={async () => { if (seasonToActivate) await handleSetCurrentSeason(seasonToActivate); setSeasonToActivate(null) }} onCancel={() => setSeasonToActivate(null)} />
      <ConfirmDialog isOpen={beltHistoryToDelete !== null} title="Supprimer le passage de ceinture" message="Cette entrée sera retirée de l’historique du membre." confirmLabel="Supprimer" onConfirm={async () => { if (beltHistoryToDelete !== null) await handleDeleteBeltHistory(beltHistoryToDelete); setBeltHistoryToDelete(null) }} onCancel={() => setBeltHistoryToDelete(null)} />
    </div>
  )
}

export default MembersAdminModule
